import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ModuleFlow, handleError } from '../../../../shared/utils/error.utils';
import { buildHttpParams } from '../../../../shared/utils/http.utils';
import { UserRepository } from '../../domain/repository';
import { ApiResponse } from '../../../../shared/dto/response/api-response.dto';
import { getApiBaseUrl } from '../../../../shared/config/runtime-config';
import {
  UserFindRequest,
  UserFindResponse,
  UserSearchRequest,
  UserSearchResponse,
  UserSaveRequest,
  UserUpdateRequest,
  UserSaveResponse,
  UserRemoveRequest,
  UserRemoveResponse,
  UserFindAllRequest,
  UserFindAllResponse,
  UserProfileRequest,
  UserProfileResponse,
  UserDashboardResponse,
  UserActivateRequest,
  UserActivateResponse,
  UserDeactivateRequest,
  UserDeactivateResponse,
  UserResetPasswordRequest,
  UserResetPasswordResponse,
  UserUnlockRequest,
  UserUnlockResponse,
} from '../../domain/dto';
import { User } from '../../domain/model';

interface UserApiListResponse {
  items: Array<Record<string, unknown>>;
  page: number;
  pageSize: number;
  totalCount: number;
}

@Injectable({ providedIn: 'root' })
export class UserRepositoryImpl implements UserRepository {
  constructor(private http: HttpClient) {}

  findAllPath(): string {
    return this.apiUrl;
  }

  dashboard(): Observable<HttpResponse<UserDashboardResponse>> {
    return this.http
      .get<ApiResponse<UserDashboardResponse>>(`${this.apiUrl}/dashboard`, { observe: 'response' })
      .pipe(
        map((response) => response.clone({ body: response.body?.data })),
        catchError((error) => handleError(ModuleFlow.UserFind, error)),
      );
  }

  findAll(payload: UserFindAllRequest): Observable<HttpResponse<UserFindAllResponse>> {
    const params = buildHttpParams(payload);

    return this.http
      .get<ApiResponse<UserApiListResponse>>(this.findAllPath(), {
        observe: 'response',
        params,
      })
      .pipe(
        map((response) => {
          const data = response.body?.data;
          const mappedItems = (data?.items ?? []).map((item) => this.mapApiUser(item));
          const mappedBody: UserFindAllResponse = {
            items: mappedItems,
            page: data?.page ?? 1,
            pageSize: data?.pageSize ?? 50,
            totalCount: data?.totalCount ?? 0,
          };

          return response.clone({ body: mappedBody });
        }),
        catchError((error) => handleError(ModuleFlow.UserFindAll, error)),
      );
  }

  find(payload: UserFindRequest): Observable<HttpResponse<UserFindResponse>> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(`${this.apiUrl}/${payload.id}`, { observe: 'response' })
      .pipe(
        map((response) => {
          const user = this.mapApiUser(response.body?.data);
          return response.clone({ body: user });
        }),
        catchError((error) => handleError(ModuleFlow.UserFind, error)),
      );
  }

  search(payload: UserSearchRequest): Observable<HttpResponse<UserSearchResponse>> {
    const params = buildHttpParams(payload);

    return this.http
      .get<ApiResponse<Array<Record<string, unknown>>>>(`${this.apiUrl}/search`, {
        observe: 'response',
        params,
      })
      .pipe(
        map((response) => {
          const list = Array.isArray(response.body?.data) ? response.body.data : [];
          const mapped = list.map((item) => this.mapApiUser(item));
          return response.clone({ body: mapped });
        }),
        catchError((error) => handleError(ModuleFlow.UserFind, error)),
      );
  }

  save(payload: UserSaveRequest): Observable<HttpResponse<UserSaveResponse>> {
    const isEdit = this.isUserUpdateRequest(payload);
    const body = this.mapSavePayload(payload);

    const request$ =
      isEdit
        ? this.http.put<ApiResponse<UserSaveResponse>>(`${this.apiUrl}/${payload.id}`, body, {
            observe: 'response',
          })
        : this.http.post<ApiResponse<UserSaveResponse>>(this.apiUrl, body, {
            observe: 'response',
          });

    return request$.pipe(
      map((response) => response.clone({ body: response.body?.data ?? {} })),
      catchError((error) => handleError(ModuleFlow.UserSave, error)),
    );
  }

  profile(payload: UserProfileRequest): Observable<HttpResponse<UserProfileResponse>> {
    return this.http
      .post<ApiResponse<UserProfileResponse>>(`${this.apiUrl}/profile` as const, payload, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: response.body?.data })),
        catchError((error) => handleError(ModuleFlow.UserSave, error)),
      );
  }

  remove(payload: UserRemoveRequest): Observable<HttpResponse<UserRemoveResponse>> {
    return this.http.delete<ApiResponse<UserRemoveResponse>>(`${this.apiUrl}/${payload.id}`, {
      observe: 'response',
    }).pipe(
      map((response) => response.clone({ body: response.body?.data })),
      catchError((error) => handleError(ModuleFlow.UserRemove, error)),
    );
  }

  activate(payload: UserActivateRequest): Observable<HttpResponse<UserActivateResponse>> {
    return this.http
      .patch<ApiResponse<UserActivateResponse>>(`${this.apiUrl}/${payload.id}/activate`, {}, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: response.body?.data ?? {} })),
        catchError((error) => handleError(ModuleFlow.UserSave, error)),
      );
  }

  deactivate(payload: UserDeactivateRequest): Observable<HttpResponse<UserDeactivateResponse>> {
    return this.http
      .patch<ApiResponse<UserDeactivateResponse>>(`${this.apiUrl}/${payload.id}/deactivate`, {}, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: response.body?.data ?? {} })),
        catchError((error) => handleError(ModuleFlow.UserSave, error)),
      );
  }

  resetPassword(
    payload: UserResetPasswordRequest,
  ): Observable<HttpResponse<UserResetPasswordResponse>> {
    return this.http
      .post<ApiResponse<UserResetPasswordResponse>>(
        `${this.apiUrl}/${payload.id}/reset-password`,
        { url: payload.url },
        { observe: 'response' },
      )
      .pipe(
        map((response) => response.clone({ body: response.body?.data ?? {} })),
        catchError((error) => handleError(ModuleFlow.UserSave, error)),
      );
  }

  unlock(payload: UserUnlockRequest): Observable<HttpResponse<UserUnlockResponse>> {
    return this.http
      .post<ApiResponse<UserUnlockResponse>>(`${this.apiUrl}/${payload.id}/unlock`, {}, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: response.body?.data ?? {} })),
        catchError((error) => handleError(ModuleFlow.UserSave, error)),
      );
  }

  private mapSavePayload(payload: UserSaveRequest): Record<string, unknown> {
    const requestBody = this.isUserUpdateRequest(payload)
      ? {
          idRole: payload.roleId,
          email: payload.email,
        }
      : {
          idRole: payload.roleId,
          idNationalityType: payload.idNationalityType,
          identification: payload.identification,
          name: payload.name,
          lastName: payload.lastName,
          email: payload.email,
        };

    const sanitizedPayload = { ...requestBody } as Record<string, unknown>;
    Object.keys(sanitizedPayload).forEach((key) => {
      if (
        sanitizedPayload[key] === null ||
        sanitizedPayload[key] === undefined ||
        sanitizedPayload[key] === ''
      ) {
        delete sanitizedPayload[key];
      }
    });

    return sanitizedPayload;
  }

  private mapApiUser(apiUser: unknown): User {
    const user = this.toRecord(apiUser);

    return {
      id: this.toNullableNumber(user['id']),
      username: this.toNullableString(user['username']),
      identification: this.toNullableString(user['identification']),
      nationalityId: this.toNullableNumber(user['nationalityId']),
      nationality: this.toNullableString(user['nationality']),
      name: this.toNullableString(user['name']),
      lastName: this.toNullableString(user['lastName']),
      email: this.toNullableString(user['email']),
      roleId: this.toNullableNumber(user['roleId']),
      roleName: this.toNullableString(user['roleName']),
      status: this.toNullableString(user['status']),
      canDelete: this.toNullableBoolean(user['canDelete']),
      created: this.toNullableString(user['created']),
      updated: this.toNullableString(user['updated']),
    };
  }

  private isUserUpdateRequest(payload: UserSaveRequest): payload is UserUpdateRequest {
    return typeof (payload as UserUpdateRequest).id === 'number';
  }

  private toNullableNumber(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toNullableString(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const parsed = String(value).trim();
    return parsed.length > 0 ? parsed : null;
  }

  private toNullableBoolean(value: unknown): boolean | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true') return true;
      if (normalized === 'false') return false;
      return null;
    }

    return null;
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private get apiUrl(): string {
    return getApiBaseUrl('users');
  }
}


